import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { positionMenuAtPoint } from '../utils/menuPosition';
import { track as trackEvent } from '../utils/analytics';
import './TrackContextMenu.scss';

// OS-style right-click menu for a MULTI-row selection. Single-row right-clicks
// go through TrackContextMenu (via RowActions `openAtPoint`) so they always
// match the ⋯ menu; this menu carries only the actions that make sense in
// bulk. Styled with the same .track-context-dropdown classes so both menus
// look identical.
//
// `items`: [{ label, icon?, danger?, onClick } | { divider: true }]
// `analyticsFrom`: surface tag, `count`: selection size (both for analytics).
const BulkRowContextMenu = ({ x, y, items, onClose, analyticsFrom, count }) => {
    const menuRef = useRef(null);
    // null while the menu measures itself on the first (hidden) paint.
    const [pos, setPos] = useState(null);

    useLayoutEffect(() => {
        if (menuRef.current) setPos(positionMenuAtPoint(menuRef.current, x, y));
    }, [x, y]);

    useEffect(() => {
        trackEvent('track_context_menu_opened', {
            trigger: 'right_click',
            bulk_count: count,
            ...(analyticsFrom ? { from: analyticsFrom } : {}),
        });
        const onEsc = (e) => { if (e.key === 'Escape') onClose(); };
        document.addEventListener('keydown', onEsc);
        return () => document.removeEventListener('keydown', onEsc);
        // Fire the open event once per mount — the host remounts per open.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return ReactDOM.createPortal(
        <div
            style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 10000 }}
            onClick={onClose}
            onContextMenu={(e) => { e.preventDefault(); onClose(); }}
        >
            <div
                ref={menuRef}
                className="track-context-dropdown"
                style={{
                    position: 'fixed',
                    left: pos ? pos.left : x,
                    top: pos ? pos.top : y,
                    // Clear the .track-context-dropdown SCSS defaults
                    // (`top: 100%; right: 0`) — left+right together stretch the
                    // menu to the viewport edge and break the measured flip.
                    right: 'auto',
                    bottom: 'auto',
                    visibility: pos ? 'visible' : 'hidden',
                    zIndex: 10001,
                }}
                onClick={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
            >
                {items.map((item, i) => item.divider ? (
                    <div key={i} className="dropdown-divider" />
                ) : (
                    <div
                        key={i}
                        className={`dropdown-item${item.danger ? ' danger' : ''}`}
                        onClick={(e) => { e.stopPropagation(); onClose(); item.onClick(); }}
                    >
                        {item.icon && <span className="dropdown-icon">{item.icon}</span>}
                        {item.label}
                    </div>
                ))}
            </div>
        </div>,
        document.body
    );
};

export default BulkRowContextMenu;
